import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  GraduationCap,
  CheckCircle2,
  Target,
  BookOpen,
  Lightbulb,
  ArrowLeft,
} from 'lucide-react';
import {
  ChercheurIcon,
  DoctorantIcon,
  EtudiantIcon,
  ProfesseurIcon,
} from '@/components/chercheurs/ProfileIcons';
import {
  DossiersTechniquesIcon,
  InterventionsIcon,
} from '@/components/prestations/ServiceIcons';
import { SEOHead } from '@/components/SEO/SEOHead';

export function ChercheursPage() {
  const { locale } = useParams<{ locale: string }>();
  const { t, i18n } = useTranslation('chercheurs');
  const currentLocale = locale || i18n.language || 'fr';

  const services = t('services.items', { returnObjects: true }) as Array<{
    title: string;
    description: string;
    features: string[];
  }>;

  const servicesWithIcons = [
    { ...services[0], icon: DossiersTechniquesIcon, color: '#9B59B6' },
    { ...services[1], icon: InterventionsIcon, color: '#8B6F47' },
  ];

  const profilesData = t('forWho.profiles', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  const profiles = [
    { ...profilesData[0], icon: ChercheurIcon },
    { ...profilesData[1], icon: DoctorantIcon },
    { ...profilesData[2], icon: EtudiantIcon },
    { ...profilesData[3], icon: ProfesseurIcon },
  ];

  const benefitsData = t('benefits.items', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  const benefits = [
    { ...benefitsData[0], icon: Target },
    { ...benefitsData[1], icon: BookOpen },
  ];

  const useCases = t('useCases.cases', { returnObjects: true }) as Array<{
    title: string;
    description: string;
    actions: string[];
  }>;

  const steps = t('howTo.steps', { returnObjects: true }) as Array<{
    number: string;
    title: string;
    description: string;
  }>;

  return (
    <div>
      <SEOHead
        title={t('hero.title')}
        description={t('hero.description')}
        path="/chercheurs"
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#9B59B6]/10 via-[#9B59B6]/5 to-white py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left: Title & Description */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Link
                  to={`/${currentLocale}`}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#9B59B6]/10 text-[#9B59B6] hover:bg-[#9B59B6]/20 transition-colors flex-shrink-0"
                  aria-label="Retour à l'accueil"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Link>
                <div className="inline-flex items-center gap-2 bg-[#9B59B6]/10 text-[#9B59B6] px-4 py-2 rounded-full text-sm font-semibold">
                  <GraduationCap className="w-4 h-4" />
                  {t('hero.badge')}
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-heading font-extrabold mb-4">
                <span className="text-aspexit-black">{t('hero.title')}</span>
                <br />
                <span className="text-[#9B59B6]">{t('hero.subtitle')}</span>
              </h1>

              <p className="text-lg text-aspexit-gray mb-4">
                {t('hero.description')}
              </p>

              <div className="flex flex-wrap gap-3 text-sm text-aspexit-gray mb-6">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#9B59B6]" />
                  {t('hero.highlights.scientific')}
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#9B59B6]" />
                  {t('hero.highlights.independent')}
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#9B59B6]" />
                  {t('hero.highlights.critical')}
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-[#9B59B6] hover:bg-[#9B59B6]/90">
                  <Link to={`/${currentLocale}/prestations`}>
                    {t('hero.buttons.primary')}
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-[#9B59B6] text-[#9B59B6] hover:bg-[#9B59B6]/10">
                  <Link to={`/${currentLocale}/contact`}>{t('hero.buttons.secondary')}</Link>
                </Button>
              </div>
            </div>

            {/* Right: Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4 bg-white border border-border shadow-sm">
                <div className="text-3xl font-bold text-[#9B59B6] mb-1">
                  PhD
                </div>
                <div className="text-sm text-aspexit-gray">
                  {t('hero.stats.phd')}
                </div>
              </Card>
              <Card className="p-4 bg-white border border-border shadow-sm">
                <div className="text-3xl font-bold text-[#9B59B6] mb-1">
                  10+
                </div>
                <div className="text-sm text-aspexit-gray">{t('hero.stats.publications')}</div>
              </Card>
              <Card className="p-4 bg-white border border-border shadow-sm">
                <div className="text-3xl font-bold text-[#9B59B6] mb-1">
                  60+
                </div>
                <div className="text-sm text-aspexit-gray">{t('hero.stats.dossiers')}</div>
              </Card>
              <Card className="p-4 bg-white border border-border shadow-sm">
                <div className="text-3xl font-bold text-[#9B59B6] mb-1">
                  50+
                </div>
                <div className="text-sm text-aspexit-gray">{t('hero.stats.conferences')}</div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <section className="py-16 bg-aspexit-gray-light">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#9B59B6]/10 text-[#9B59B6] px-3 py-1 rounded-full text-sm font-medium mb-4">
              <GraduationCap className="w-4 h-4" />
              {t('forWho.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black">
              {t('forWho.title')}
            </h2>
            <p className="text-lg text-aspexit-gray max-w-3xl mx-auto mt-4">
              {t('forWho.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {profiles.map((profile, index) => {
              const IconComponent = profile.icon;
              return (
                <Card
                  key={index}
                  className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-2xl bg-[#9B59B6]/10 flex items-center justify-center">
                      <IconComponent className="w-10 h-10" color="#9B59B6" />
                    </div>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-aspexit-black mb-2">
                    {profile.title}
                  </h3>
                  <p className="text-sm text-aspexit-gray min-h-[3rem]">
                    {profile.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black">
              {t('services.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {servicesWithIcons.map((service) => {
              const Icon = service.icon;
              return (
                <Card
                  key={service.title}
                  className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
                >
                  <div
                    className="w-16 h-16 rounded-lg flex items-center justify-center mb-4 flex-shrink-0"
                    style={{ backgroundColor: `${service.color}15` }}
                  >
                    <Icon className="w-10 h-10" color={service.color} />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-aspexit-black mb-3">
                    {service.title}
                  </h3>
                  <p className="text-aspexit-gray mb-6 leading-relaxed flex-1">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-2 text-sm text-aspexit-gray"
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#9B59B6] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline" className="border-[#9B59B6] text-[#9B59B6] hover:bg-[#9B59B6]/10">
              <Link to={`/${currentLocale}/prestations`}>
                {t('services.button')}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-aspexit-gray-light">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black">
              {t('useCases.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-heading font-bold text-aspexit-black mb-3">
                  {useCase.title}
                </h3>
                <p className="text-aspexit-gray mb-6">{useCase.description}</p>
                <ul className="space-y-2">
                  {useCase.actions.map((action, actionIndex) => (
                    <li
                      key={actionIndex}
                      className="flex items-center gap-2 text-sm text-aspexit-gray"
                    >
                      <CheckCircle2 className="w-4 h-4 text-[#9B59B6] flex-shrink-0" />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black">
              {t('benefits.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#9B59B6]/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-[#9B59B6]" />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-aspexit-black mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-aspexit-gray">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Start */}
      <section className="py-16 bg-aspexit-gray-light">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black mb-4">
              {t('howTo.title')}
            </h2>
            <p className="text-lg text-aspexit-gray">
              {t('howTo.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 rounded-full bg-white border-4 border-[#9B59B6] flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#9B59B6]">
                    {step.number}
                  </span>
                </div>
                <h3 className="text-lg font-heading font-bold text-aspexit-black mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-aspexit-gray">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#9B59B6] to-[#9B59B6]/80 text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Lightbulb className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t('cta.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white text-[#9B59B6] hover:bg-aspexit-gray-light"
            >
              <Link to={`/${currentLocale}/prestations`}>
                {t('cta.buttons.primary')}
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              className="bg-white text-[#9B59B6] hover:bg-aspexit-gray-light"
            >
              <Link to={`/${currentLocale}/contact`}>{t('cta.buttons.secondary')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
